import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RouteIcon from '@mui/icons-material/Route';
import SearchIcon from '@mui/icons-material/Search';
import {DndProvider, useDrag, useDrop} from 'react-dnd';
import {HTML5Backend} from 'react-dnd-html5-backend';
import {useTranslation} from 'react-i18next';
import StandardDialog from '../dialogs/base/standardDialog';
import CapacityBar from './CapacityBar';
import {GroupageTripNegotiation, GroupageTripStatus, GroupageTripWithNegotiations} from '../../models/groupageTrip';
import {type LoadingPlace, LoadingPlaceTypes} from '../../models/booking';
import {OfferStatus} from '../../types/vehicleOffer';
import {
  getGroupageTrip,
  reorderGroupageTripNegotiations,
  removeNegotiationFromTrip,
  updateGroupageTripStatus,
} from '../../services/groupageTripService';
import {toast} from '../../utils/toast';

const DRAG_TYPE = 'GROUPAGE_NEGOTIATION';

interface GroupageTripDialogProps {
  open: boolean;
  onClose: () => void;
  vehicleId: number;
  tripId: number;
  onFindMoreCargo: () => void;
  onTripChanged?: () => void;
  onNegotiationClick?: (negotiationId: string) => void;
}

const STATUS_LABELS: Record<GroupageTripStatus, string> = {
  [GroupageTripStatus.ACTIVE]: 'Active',
  [GroupageTripStatus.FULL]: 'Full',
  [GroupageTripStatus.IN_PROGRESS]: 'In Progress',
  [GroupageTripStatus.COMPLETED]: 'Completed',
  [GroupageTripStatus.CANCELLED]: 'Cancelled',
};

const STATUS_COLORS: Record<GroupageTripStatus, 'success' | 'info' | 'warning' | 'default' | 'error'> = {
  [GroupageTripStatus.ACTIVE]: 'success',
  [GroupageTripStatus.FULL]: 'warning',
  [GroupageTripStatus.IN_PROGRESS]: 'info',
  [GroupageTripStatus.COMPLETED]: 'default',
  [GroupageTripStatus.CANCELLED]: 'error',
};

const EDITABLE_STATUSES: GroupageTripStatus[] = [
  GroupageTripStatus.ACTIVE,
  GroupageTripStatus.FULL,
  GroupageTripStatus.IN_PROGRESS,
  GroupageTripStatus.COMPLETED,
];

interface RouteStop {
  city: string;
  country: string;
  type: 'L' | 'U';
}

const buildRouteStops = (negotiations: GroupageTripNegotiation[]): RouteStop[] => {
  const loadings: RouteStop[] = [];
  const unloadings: RouteStop[] = [];
  for(const nego of negotiations) {
    const raw = nego.loadingPlaces;
    const places: LoadingPlace[] = Array.isArray(raw) ? raw : (typeof raw === 'string' ? JSON.parse(raw || '[]') : []);
    for(const lp of places) {
      const addr = lp.address;
      if(!addr?.city) continue;
      const stop: RouteStop = {city: addr.city, country: addr.country || '?', type: lp.loadingType === LoadingPlaceTypes.LOADING ? 'L' : 'U'};
      if(lp.loadingType === LoadingPlaceTypes.LOADING) loadings.push(stop);
      else unloadings.push(stop);
    }
  }
  return [...loadings, ...unloadings.reverse()];
};

const getRouteLabel = (nego: GroupageTripNegotiation): string => {
  const raw = nego.loadingPlaces;
  const places: LoadingPlace[] = Array.isArray(raw) ? raw : (typeof raw === 'string' ? JSON.parse(raw || '[]') : []);
  const loading = places.find(p => p.loadingType === LoadingPlaceTypes.LOADING);
  const unloading = places.find(p => p.loadingType === LoadingPlaceTypes.UNLOADING);
  const from = loading?.address ? `${loading.address.city}, ${loading.address.country}` : '?';
  const to = unloading?.address ? `${unloading.address.city}, ${unloading.address.country}` : '?';
  return `${from} → ${to}`;
};

const NEGO_STATUS_LABELS: Partial<Record<OfferStatus, string>> = {
  [OfferStatus.NEGOTIATION_STARTED]: 'Started',
  [OfferStatus.NEGOTIATION_PRICE_RECEIVED]: 'Price received',
  [OfferStatus.NEGOTIATION_PRICE_OFFERED]: 'Price offered',
  [OfferStatus.NEGOTIATION_ACCEPTED]: 'Accepted',
  [OfferStatus.NEGOTIATION_DECLINED]: 'Declined',
  [OfferStatus.NEGOTIATION_NEED_HUMAN]: 'Needs review',
  [OfferStatus.NEGOTIATION_WAITING_FINAL_CONFIRM]: 'Awaiting confirm',
  [OfferStatus.NEGOTIATION_MANUALLY_CLOSED]: 'Closed',
  [OfferStatus.NEGOTIATION_BACK_CONFIRMED]: 'Confirmed',
  [OfferStatus.NEGOTIATION_MANUALLY_CLOSED_UNDISCLOSED]: 'Closed',
};

const NEGO_STATUS_COLORS: Partial<Record<OfferStatus, 'success' | 'info' | 'warning' | 'error' | 'default'>> = {
  [OfferStatus.NEGOTIATION_STARTED]: 'info',
  [OfferStatus.NEGOTIATION_PRICE_RECEIVED]: 'info',
  [OfferStatus.NEGOTIATION_PRICE_OFFERED]: 'info',
  [OfferStatus.NEGOTIATION_ACCEPTED]: 'success',
  [OfferStatus.NEGOTIATION_DECLINED]: 'error',
  [OfferStatus.NEGOTIATION_NEED_HUMAN]: 'warning',
  [OfferStatus.NEGOTIATION_WAITING_FINAL_CONFIRM]: 'warning',
  [OfferStatus.NEGOTIATION_MANUALLY_CLOSED]: 'default',
  [OfferStatus.NEGOTIATION_BACK_CONFIRMED]: 'success',
  [OfferStatus.NEGOTIATION_MANUALLY_CLOSED_UNDISCLOSED]: 'default',
};

const getNegoPrice = (nego: GroupageTripNegotiation): {label: string; value: number} | null => {
  if(nego.lastOfferedPriceClient) return {label: 'Client', value: nego.lastOfferedPriceClient};
  if(nego.lastOfferedPriceUs) return {label: 'Ours', value: nego.lastOfferedPriceUs};
  if(nego.initiallyOfferedPrice) return {label: 'Client', value: nego.initiallyOfferedPrice};
  if(nego.lastEmailOfferedPrice) return {label: 'Client', value: nego.lastEmailOfferedPrice};
  const fe = nego.fePrice as {value?: number} | null;
  if(fe?.value) return {label: 'Listed', value: fe.value};
  return null;
};

const getTimeWindow = (nego: GroupageTripNegotiation, type: 'LOADING' | 'UNLOADING'): string | null => {
  const raw = nego.loadingPlaces;
  const places: LoadingPlace[] = Array.isArray(raw) ? raw : (typeof raw === 'string' ? JSON.parse(raw || '[]') : []);
  const lp = places.find(p => p.loadingType === type);
  if(!lp) return null;
  const date = lp.earliestLoadingDate || lp.latestLoadingDate;
  const time = lp.startTime && lp.endTime ? `${lp.startTime}-${lp.endTime}` : lp.startTime || '';
  return date ? `${date}${time ? ' ' + time : ''}` : null;
};

interface DraggableNegoItemProps {
  nego: GroupageTripNegotiation;
  index: number;
  moveNego: (from: number, to: number) => void;
  onDrop: () => void;
  canDrag: boolean;
  canRemove: boolean;
  removingId: string | null;
  onRemove: (id: string) => void;
  onNavigate?: (id: string) => void;
}

const DraggableNegoItem: React.FC<DraggableNegoItemProps> = ({nego, index, moveNego, onDrop, canDrag, canRemove, removingId, onRemove, onNavigate}) => {
  const {t} = useTranslation();
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const [{isDragging}, drag, preview] = useDrag({
    type: DRAG_TYPE,
    item: () => ({index}),
    canDrag,
    collect: (monitor) => ({isDragging: monitor.isDragging()}),
    end: (_item, monitor) => {
      if(monitor.didDrop()) onDrop();
    },
  });

  const [, drop] = useDrop({
    accept: DRAG_TYPE,
    drop: () => ({}),
    hover: (item: {index: number}, monitor) => {
      if(!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if(dragIndex === hoverIndex) return;
      const hoverRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverRect.bottom - hoverRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      if(!clientOffset) return;
      const hoverClientY = clientOffset.y - hoverRect.top;
      if(dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if(dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;
      moveNego(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  preview(drop(ref));

  return (
    <Box
      ref={ref}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        opacity: isDragging ? 0.4 : 1,
        bgcolor: isDragging ? 'action.hover' : 'transparent',
      }}
    >
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0}}>
        {canDrag && (
          <Box ref={drag} sx={{cursor: 'grab', display: 'flex', color: 'text.secondary', alignSelf: 'stretch', alignItems: 'center'}}>
            <DragIndicatorIcon fontSize="small" />
          </Box>
        )}
        <Box sx={{minWidth: 0, flex: 1}}>
          <Typography variant="body2" fontWeight={600} noWrap>{getRouteLabel(nego)}</Typography>
          <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
            <Typography variant="caption" color="text.secondary">
              {nego.weightT?.toFixed(1)}T · {nego.lengthM?.toFixed(1)}m
            </Typography>
            {getTimeWindow(nego, LoadingPlaceTypes.LOADING) && (
              <Typography variant="caption" color="text.secondary">
                L: {getTimeWindow(nego, LoadingPlaceTypes.LOADING)}
              </Typography>
            )}
            {getTimeWindow(nego, LoadingPlaceTypes.UNLOADING) && (
              <Typography variant="caption" color="text.secondary">
                U: {getTimeWindow(nego, LoadingPlaceTypes.UNLOADING)}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0}}>
        {getNegoPrice(nego) && (
          <Typography variant="caption" sx={{fontWeight: 600, mr: 0.5}}>
            {getNegoPrice(nego)!.value.toFixed(0)}€
          </Typography>
        )}
        <Chip
          size="small"
          label={NEGO_STATUS_LABELS[nego.offerStatus as OfferStatus] || '?'}
          color={NEGO_STATUS_COLORS[nego.offerStatus as OfferStatus] || 'default'}
          sx={{fontSize: '0.7rem', height: 20, borderRadius: 1}}
        />
        {onNavigate && (
          <Tooltip title={t('Open negotiation')}>
            <IconButton size="small" onClick={() => onNavigate(nego.id)} sx={{ml: 0.5}}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {canRemove && (
          <Tooltip title={t('Remove from trip')}>
            <span>
              <IconButton
                size="small"
                color="error"
                disabled={removingId !== null}
                onClick={() => confirmRemoveId === nego.id ? onRemove(nego.id) : setConfirmRemoveId(nego.id)}
              >
                {removingId === nego.id ? <CircularProgress size={16} /> : <DeleteOutlineIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
        {confirmRemoveId === nego.id && removingId !== nego.id && (
          <Typography variant="caption" color="error" sx={{cursor: 'pointer', whiteSpace: 'nowrap'}} onClick={() => setConfirmRemoveId(null)}>
            {t('Cancel')}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const GroupageTripDialog: React.FC<GroupageTripDialogProps> = ({
  open,
  onClose,
  vehicleId,
  tripId,
  onFindMoreCargo,
  onTripChanged,
  onNegotiationClick,
}) => {
  const {t} = useTranslation();
  const [trip, setTrip] = useState<GroupageTripWithNegotiations | null>(null);
  const [negotiations, setNegotiations] = useState<GroupageTripNegotiation[]>([]);
  const [loading, setLoading] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const loadTrip = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getGroupageTrip(vehicleId, tripId);
      setTrip(data);
      setNegotiations(data.negotiations);
    } catch(_err) {
      // error already toasted by service
    } finally {
      setLoading(false);
    }
  }, [vehicleId, tripId]);

  useEffect(() => {
    if(open) loadTrip();
  }, [open, loadTrip]);

  const handleRemoveNegotiation = async (negotiationId: string): Promise<void> => {
    setRemovingId(negotiationId);
    try {
      const updated = await removeNegotiationFromTrip(vehicleId, tripId, negotiationId);
      setTrip(prev => prev ? {...prev, ...updated, negotiations: prev.negotiations.filter(n => n.id !== negotiationId)} : prev);
      setNegotiations(prev => prev.filter(n => n.id !== negotiationId));
      onTripChanged?.();
    } catch(_err) {
      // error already toasted by service
    } finally {
      setRemovingId(null);
    }
  };

  const handleStatusChange = async (event: SelectChangeEvent<GroupageTripStatus>): Promise<void> => {
    const newStatus = Number(event.target.value) as GroupageTripStatus;
    try {
      const updated = await updateGroupageTripStatus(vehicleId, tripId, newStatus);
      setTrip(prev => prev ? {...prev, ...updated} : prev);
      toast.success(t('Groupage trip status updated'));
      onTripChanged?.();
    } catch(_err) {
      // error already toasted by service
    }
  };

  const moveNego = useCallback((fromIndex: number, toIndex: number) => {
    setNegotiations(prev => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      return updated;
    });
  }, []);

  const handleReorderDrop = useCallback(async () => {
    setReordering(true);
    try {
      const result = await reorderGroupageTripNegotiations(vehicleId, tripId, negotiations.map(n => n.id));
      setTrip(result);
      setNegotiations(result.negotiations);
      onTripChanged?.();
    } catch(_err) {
      // Revert on failure
      if(trip) setNegotiations(trip.negotiations);
    } finally {
      setReordering(false);
    }
  }, [vehicleId, tripId, negotiations, trip, onTripChanged]);

  const isCancelled = trip?.status === GroupageTripStatus.CANCELLED;
  const isCompleted = trip?.status === GroupageTripStatus.COMPLETED;
  const canReorder = !isCancelled && !isCompleted && negotiations.length > 1;
  const canRemove = !isCompleted; // Allow removal for CANCELLED too - negotiations can be reused

  return (
    <StandardDialog
      open={open}
      onClose={onClose}
      title={t('Groupage Trip #{{id}}', {id: tripId})}
      autoSize
      maxWidth="sm"
      tertiaryAction={!isCancelled ? {
        label: t('Find more cargo'),
        startIcon: <SearchIcon />,
        onClick: onFindMoreCargo,
      } : undefined}
    >
      {loading && (
        <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}>
          <CircularProgress size={32} />
        </Box>
      )}
      {!loading && trip && (
        <Box sx={{p: 3, display: 'flex', flexDirection: 'column', gap: 3}}>
          {/* Status */}
          <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
            <Typography variant="body2" color="text.secondary" sx={{minWidth: 60}}>{t('Status')}</Typography>
            {isCancelled ? (
              <Chip label={STATUS_LABELS[trip.status]} color="error" size="small" />
            ) : (
              <Select<GroupageTripStatus>
                size="small"
                value={trip.status}
                onChange={handleStatusChange}
                sx={{minWidth: 160}}
                renderValue={(val) => (
                  <Chip
                    label={STATUS_LABELS[val as GroupageTripStatus]}
                    color={STATUS_COLORS[val as GroupageTripStatus]}
                    size="small"
                    sx={{cursor: 'pointer'}}
                  />
                )}
              >
                {EDITABLE_STATUSES.map(s => (
                  <MenuItem key={s} value={s}>{STATUS_LABELS[s]}</MenuItem>
                ))}
              </Select>
            )}
          </Box>

          {/* Capacity */}
          <Box>
            <Typography variant="subtitle2" sx={{mb: 1.5}}>{t('Capacity')}</Typography>
            <CapacityBar
              usedWeightT={trip.totalWeightT}
              maxWeightT={trip.totalWeightT + trip.remainingWeightT}
              usedLengthM={trip.totalLengthM}
              maxLengthM={trip.totalLengthM + trip.remainingLengthM}
            />
          </Box>

          {/* Combined Route Summary */}
          {negotiations.length > 0 && (
            <>
              <Divider />
              <Box>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1.5}}>
                  <RouteIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2">{t('Combined Route')}</Typography>
                  {reordering && <CircularProgress size={14} />}
                </Box>
                {/* Route stops chain */}
                <Typography variant="body2" sx={{mb: 1.5, lineHeight: 1.8}}>
                  {buildRouteStops(negotiations).map((stop, i, arr) => {
                    const isTransition = i > 0 && arr[i - 1].type === 'L' && stop.type === 'U';
                    const color = stop.type === 'L' ? 'success.main' : 'warning.dark';
                    return (
                      <React.Fragment key={i}>
                        {isTransition && <Box component="span" sx={{color: 'text.secondary'}}>{' \u2192 '}</Box>}
                        {stop.type === 'L' && <Box component="span" sx={{color: 'text.secondary'}}>{'\u21B3\u2009'}</Box>}
                        <Box component="span" sx={{color, fontWeight: 500}}>{stop.city}</Box>
                        {stop.type === 'U' && <Box component="span" sx={{color: 'text.secondary'}}>{'\u2009\u21B3'}</Box>}
                      </React.Fragment>
                    );
                  })}
                </Typography>
                {!reordering && (
                  <Box sx={{display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1}}>
                    <Box sx={{display: 'flex', gap: 3}}>
                      {typeof trip.combinedDistanceKms === 'number' ? (
                        <Typography variant="body2" color="text.secondary">
                          {t('Distance')}: <strong>{Math.round(trip.combinedDistanceKms)} km</strong>
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{fontStyle: 'italic', display: 'inline-flex', alignItems: 'center', gap: 1}}>
                          <CircularProgress size={12} />
                          {t('Distance: calculating…')}
                        </Typography>
                      )}
                      {typeof trip.combinedEstimatedPrice === 'number' && (
                        <Typography variant="body2" color="text.secondary">
                          {t('Route cost')}: <strong>{trip.combinedEstimatedPrice.toFixed(0)} EUR</strong>
                        </Typography>
                      )}
                    </Box>
                    {(() => {
                      const totalRevenue = negotiations.reduce((sum, n) => sum + (getNegoPrice(n)?.value || 0), 0);
                      const margin = totalRevenue && typeof trip.combinedEstimatedPrice === 'number' ? totalRevenue - trip.combinedEstimatedPrice : undefined;
                      return (
                        <Box sx={{display: 'flex', gap: 3}}>
                          {totalRevenue ? (
                            <Typography variant="body2" color="text.secondary">
                              {t('Revenue')}: <strong>{totalRevenue.toFixed(0)} EUR</strong>
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{fontStyle: 'italic'}}>
                              {t('Revenue: n/a')}
                            </Typography>
                          )}
                          {typeof margin === 'number' ? (
                            <Typography variant="body2" sx={{color: margin >= 0 ? 'success.main' : 'error.main', fontWeight: 600}}>
                              {t('Margin')}: {margin >= 0 ? '+' : ''}{margin.toFixed(0)} EUR
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{fontStyle: 'italic'}}>
                              {t('Margin: pending route')}
                            </Typography>
                          )}
                        </Box>
                      );
                    })()}
                  </Box>
                )}
              </Box>
            </>
          )}

          <Divider />

          {/* Negotiations */}
          <Box>
            <Typography variant="subtitle2" sx={{mb: 1.5}}>
              {t('Cargo ({{count}})', {count: negotiations.length})}
            </Typography>
            {negotiations.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{t('No cargo linked')}</Typography>
            ) : (
              <DndProvider backend={HTML5Backend}>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                  {negotiations.map((nego, index) => (
                    <DraggableNegoItem
                      key={nego.id}
                      nego={nego}
                      index={index}
                      moveNego={moveNego}
                      onDrop={handleReorderDrop}
                      canDrag={canReorder}
                      canRemove={canRemove}
                      removingId={removingId}
                      onRemove={handleRemoveNegotiation}
                      onNavigate={onNegotiationClick || (() => onClose())}
                    />
                  ))}
                </Box>
              </DndProvider>
            )}
          </Box>
        </Box>
      )}
    </StandardDialog>
  );
};

export default GroupageTripDialog;
